import { useEffect, useState } from "react";
import { Auth, Hub } from "aws-amplify";
import { jwtDecode } from "jwt-decode";

const parseGroups = (groupString) => {
  return (
    groupString
      ?.replace(/^\[|\]$/g, "")
      .split(",")
      .map((g) => g.trim()) || []
  );
};

export default function useAuth() {
  const [user, setUser] = useState(() => {
    const storedUser = localStorage.getItem("user");
    try {
      const parsed = storedUser ? JSON.parse(storedUser) : null;
      return typeof parsed === "string" ? parsed : parsed?.email ?? null;
    } catch {
      return null;
    }
  });
  
  const [hasAccess, setHasAccess] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(!user);

  const handleLogin = () => {
    localStorage.setItem("signedOut", "false")
    Auth.federatedSignIn({ provider: "UMassChan" }).catch((err) => {
      console.error("Federated sign-in failed:", err);
    });
  };

  const configureStateWithAuthInfo = (session) => {
    const idToken = session.getIdToken().getJwtToken();
    const decodedToken = jwtDecode(idToken);
    const userEmail = decodedToken.email;
    setUser(userEmail);

    const rawGroups = session.idToken.payload["custom:groups"];
    const parsedGroups = parseGroups(rawGroups);
    const hasAccess =
      parsedGroups.some((group) => group.startsWith("AWSBillingUsers")) ||
      parsedGroups.some((group) =>
        group.startsWith("AWSBillingAdministrators")
      );
    setHasAccess(hasAccess);

    const isAdmin = parsedGroups.some((group) =>
      group.startsWith("AWSBillingAdministrators")
    );
    setIsAdmin(isAdmin);

    localStorage.setItem("user", JSON.stringify(userEmail));
  };

  useEffect(() => {
    const checkSession = async () => {
      try {
        const session = await Auth.currentSession();
        configureStateWithAuthInfo(session)
      } catch (err) {
        console.error("Session check failed:", err);
        setUser(null);
        localStorage.removeItem("user");
      } finally {
        setLoading(false);
      }
    };
    checkSession();
  }, []);

  useEffect(() => {
    const listener = Hub.listen("auth", ({ payload: { event, data } }) => {
      switch (event) {
        case "signIn":
          Auth.currentSession()
            .then((session) => {
              configureStateWithAuthInfo(session)
              setLoading(false);
            })
            .catch((err) => console.error("Error getting session:", err));
          break;
        case "signOut":
          setUser(null);
          localStorage.removeItem("user");
          break;
        case "signIn_failure":
          console.error("Sign-in failed", data);
          setUser(null);
          break;
        default:
          break;
      }
    });

    return () => listener();
  }, [user, hasAccess]);

  useEffect(() => {
    const signedOut = localStorage.getItem("signedOut") === "true";
    if (!user && !signedOut) {
      const timer = setTimeout(() => handleLogin(), 3000);
      return () => clearTimeout(timer);
    }
  }, [user]);

  return { user, loading, hasAccess, isAdmin, handleLogin };
}
